import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { handleAsync } from 'best-modules/plugins'
import { defineLocalStore } from '@/utils/localStore'
import { documentDashboard } from '@/requests/document'
import type { DataTableHeader } from '@/utils/types/vuetify'

type DashboardPeriod = 'week' | 'month'

type DashboardColor =
  | 'blue-darken-3'
  | 'deep-purple-accent-3'
  | 'success'
  | 'warning'

type DashboardDocumentRoute =
  | 'document-list-review'
  | 'document-list-agreement-today'
  | 'document-list-agreement-terminated'
  | 'document-list-agreement-terminating'
  | 'document-list-signature-today'
  | 'document-list-signature-terminated'
  | 'document-list-signature-terminating'

type DashboardQueueItem = {
  title: string
  value: number
  routeName?: DashboardDocumentRoute
  disabled?: boolean
}

type DashboardQueueCard = {
  key: string
  title: string
  value: number
  items: DashboardQueueItem[]
}

type DashboardProcessingStat = {
  key: string
  title: string
  value: number
  diff: number
  icon: string
  color: DashboardColor
  bgClass: string
}

type DashboardDepartment = {
  id: number
  name: string
  reviewed: number
  agreed: number
  approved: number
  signed: number
}

type DashboardQueue = {
  review: number
  reviewPending: number
  agreement: number
  agreementToday: number
  agreementTerminated: number
  agreementTerminating: number
  signature: number
  signatureToday: number
  signatureTerminated: number
  signatureTerminating: number
}

type DashboardProcessing = Record<
  DashboardPeriod,
  {
    reviewed: number
    reviewedDiff: number
    agreed: number
    agreedDiff: number
    approved: number
    approvedDiff: number
    signed: number
    signedDiff: number
  }
>

type DashboardSource = {
  queue: DashboardQueue
  processing: DashboardProcessing
  departments: Record<DashboardPeriod, DashboardDepartment[]>
}

const emptyDashboardSource = (): DashboardSource => ({
  queue: {
    review: 0,
    reviewPending: 0,
    agreement: 0,
    agreementToday: 0,
    agreementTerminated: 0,
    agreementTerminating: 0,
    signature: 0,
    signatureToday: 0,
    signatureTerminated: 0,
    signatureTerminating: 0,
  },
  processing: {
    week: {
      reviewed: 0,
      reviewedDiff: 0,
      agreed: 0,
      agreedDiff: 0,
      approved: 0,
      approvedDiff: 0,
      signed: 0,
      signedDiff: 0,
    },
    month: {
      reviewed: 0,
      reviewedDiff: 0,
      agreed: 0,
      agreedDiff: 0,
      approved: 0,
      approvedDiff: 0,
      signed: 0,
      signedDiff: 0,
    },
  },
  departments: {
    week: [],
    month: [],
  },
})

const departmentHeaders: DataTableHeader[] = [
  {
    title: 'Департамент',
    key: 'name',
    sortable: false,
  },
  {
    title: 'Розгл.',
    key: 'reviewed',
    align: 'center',
    sortable: false,
  },
  {
    title: 'Погодж.',
    key: 'agreed',
    align: 'center',
    sortable: false,
  },
  {
    title: 'Затв.',
    key: 'approved',
    align: 'center',
    sortable: false,
  },
  {
    title: 'Підпис.',
    key: 'signed',
    align: 'center',
    sortable: false,
  },
  {
    title: 'Усього',
    key: 'total',
    sortable: false,
  },
]

export const {
  useLocalStore: useHomeDashboard,
  initLocalStore: initHomeDashboard,
} = defineLocalStore('home-dashboard', () => {
  const router = useRouter()
  const selectedPeriod = ref<DashboardPeriod>('week')
  const isInitialized = ref(false)
  const dashboardSource = ref<DashboardSource>(emptyDashboardSource())

  const init = () => {
    return handleAsync('initHomeDashboard', async () => {
      dashboardSource.value = await documentDashboard()
      isInitialized.value = true
    })
  }

  const queueCards = computed<DashboardQueueCard[]>(() => [
    {
      key: 'review',
      title: 'На розгляді',
      value: dashboardSource.value.queue.review,
      items: [
        {
          title: 'Очікують вашого розгляду',
          value: dashboardSource.value.queue.reviewPending,
          routeName: 'document-list-review',
        },
      ],
    },
    {
      key: 'agreement',
      title: 'На візуванні / погодженні',
      value: dashboardSource.value.queue.agreement,
      items: [
        {
          title: 'На сьогодні',
          value: dashboardSource.value.queue.agreementToday,
          routeName: 'document-list-agreement-today',
        },
        {
          title: 'Протерміновані',
          value: dashboardSource.value.queue.agreementTerminated,
          disabled: dashboardSource.value.queue.agreementTerminated === 0,
          routeName: 'document-list-agreement-terminated',
        },
        {
          title: 'Термін наближається',
          value: dashboardSource.value.queue.agreementTerminating,
          routeName: 'document-list-agreement-terminating',
        },
      ],
    },
    {
      key: 'signature',
      title: 'На підписанні',
      value: dashboardSource.value.queue.signature,
      items: [
        {
          title: 'На сьогодні',
          value: dashboardSource.value.queue.signatureToday,
          routeName: 'document-list-signature-today',
        },
        {
          title: 'Протерміновані',
          value: dashboardSource.value.queue.signatureTerminated,
          routeName: 'document-list-signature-terminated',
        },
        {
          title: 'Термін наближається',
          value: dashboardSource.value.queue.signatureTerminating,
          disabled: dashboardSource.value.queue.signatureTerminating === 0,
          routeName: 'document-list-signature-terminating',
        },
      ],
    },
    {
      key: 'control',
      title: 'На контролі',
      value: 0,
      items: [
        {
          title: 'Невиконані',
          value: 0,
        },
        {
          title: 'Термінові',
          value: 0,
        },
        {
          title: 'Прострочені',
          value: 0,
        },
      ],
    },
  ])

  const processingStats = computed<DashboardProcessingStat[]>(() => {
    const processing = dashboardSource.value.processing[selectedPeriod.value]

    return [
      {
        key: 'reviewed',
        title: 'Розглянуто',
        value: processing.reviewed,
        diff: processing.reviewedDiff,
        icon: 'mdi-eye-outline',
        color: 'blue-darken-3',
        bgClass: 'bg-blue-lighten-5',
      },
      {
        key: 'agreed',
        title: 'Погоджено',
        value: processing.agreed,
        diff: processing.agreedDiff,
        icon: 'mdi-checkbox-marked-outline',
        color: 'deep-purple-accent-3',
        bgClass: 'bg-deep-purple-lighten-5',
      },
      {
        key: 'approved',
        title: 'Затверджено',
        value: processing.approved,
        diff: processing.approvedDiff,
        icon: 'mdi-check-circle-outline',
        color: 'warning',
        bgClass: 'bg-amber-lighten-5',
      },
      {
        key: 'signed',
        title: 'Підписано',
        value: processing.signed,
        diff: processing.signedDiff,
        icon: 'mdi-fountain-pen-tip',
        color: 'success',
        bgClass: 'bg-green-lighten-5',
      },
    ]
  })

  const departmentRows = computed(() =>
    dashboardSource.value.departments[selectedPeriod.value].map(department => ({
      ...department,
      total:
        department.reviewed +
        department.agreed +
        department.approved +
        department.signed,
    }))
  )

  const maxDepartmentTotal = computed(() => {
    return Math.max(...departmentRows.value.map(item => item.total), 1)
  })

  const getProgressValue = (total: number) => {
    return (total / maxDepartmentTotal.value) * 100
  }

  const getQueueCardIcon = (key: string) => {
    if (key === 'review') return 'mdi-eye-outline'
    if (key === 'agreement') return 'mdi-checkbox-marked-outline'
    if (key === 'control') return 'mdi-clipboard-alert-outline'

    return 'mdi-fountain-pen-tip'
  }

  const getQueueCardColor = (key: string): DashboardColor => {
    if (key === 'review') return 'blue-darken-3'
    if (key === 'agreement') return 'deep-purple-accent-3'
    if (key === 'control') return 'warning'

    return 'success'
  }

  const getQueueCardBgClass = (key: string) => {
    if (key === 'review') return 'bg-blue-lighten-5'
    if (key === 'agreement') return 'bg-deep-purple-lighten-5'
    if (key === 'control') return 'bg-amber-lighten-5'

    return 'bg-green-lighten-5'
  }

  const getQueueCardTextClass = (key: string) => {
    if (key === 'review') return 'text-blue-darken-3'
    if (key === 'agreement') return 'text-deep-purple-accent-3'
    if (key === 'control') return 'text-warning'

    return 'text-success'
  }

  const openQueueItem = (item: DashboardQueueItem) => {
    if (!item.routeName || item.disabled) return

    router.push({ name: item.routeName })
  }

  init()

  return {
    isInitialized,
    selectedPeriod,
    queueCards,
    processingStats,
    departmentRows,
    departmentHeaders,
    getProgressValue,
    getQueueCardIcon,
    getQueueCardColor,
    getQueueCardBgClass,
    getQueueCardTextClass,
    openQueueItem,
  }
})
